import React, { useState } from 'react';
import { ArrowRight, Mail, User, Briefcase } from 'lucide-react';
import { validateFormData, checkRateLimit, validateHoneypot, sanitizeInput, sanitizeEmail } from '../utils/validation';
import { trackFormSubmission, trackError, trackUserInteraction } from '../utils/analytics';

interface EmailFormProps {
  onSuccess: () => void;
  darkMode?: boolean;
}

const EmailForm: React.FC<EmailFormProps> = ({ onSuccess, darkMode = false }) => {
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [honeypot, setHoneypot] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    try {
      // Honeypot validation (bot detection)
      if (!validateHoneypot(honeypot)) {
        trackError('Bot detected', 'honeypot_validation');
        throw new Error('Invalid submission');
      }

      // Rate limiting
      if (!checkRateLimit()) {
        setErrors({ general: 'Too many submissions. Please wait a moment before trying again.' });
        trackError('Rate limit exceeded', 'form_submission');
        return;
      }

      // Sanitize inputs
      const sanitizedData = {
        email: sanitizeEmail(formData.email),
        name: sanitizeInput(formData.name),
        role: sanitizeInput(formData.role)
      };

      // Validate form data
      const validation = validateFormData(sanitizedData);
      if (!validation.isValid) {
        setErrors(validation.errors);
        trackError('Validation failed', 'form_submission', validation.errors);
        return;
      }

      // Track form submission attempt
      trackFormSubmission('main-form', sanitizedData.email, {
        has_name: !!sanitizedData.name,
        has_role: !!sanitizedData.role
      });

      // In development mode, simulate success
      if (import.meta.env.DEV) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const waitlistData = {
          ...sanitizedData,
          timestamp: new Date().toISOString(),
          id: Date.now(),
          source: 'main-form'
        };
        
        const existingData = JSON.parse(localStorage.getItem('contax-waitlist') || '[]');
        existingData.push(waitlistData);
        localStorage.setItem('contax-waitlist', JSON.stringify(existingData));
        
        onSuccess();
        return;
      }

      // Production: Submit to Netlify Forms
      const response = await fetch('/', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({
          'form-name': 'contax-waitlist',
          'email': sanitizedData.email,
          'name': sanitizedData.name || '',
          'role': sanitizedData.role || '',
          'timestamp': new Date().toISOString(),
          'source': 'main-form',
          'bot-field': honeypot
        }).toString()
      });

      if (!response.ok) {
        const responseText = await response.text();
        console.error('Form submission failed:', response.status, responseText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Store in localStorage for demo purposes
      const waitlistData = {
        ...sanitizedData,
        timestamp: new Date().toISOString(),
        id: Date.now(),
        source: 'main-form'
      };
      
      const existingData = JSON.parse(localStorage.getItem('contax-waitlist') || '[]');
      existingData.push(waitlistData);
      localStorage.setItem('contax-waitlist', JSON.stringify(existingData));
      
      onSuccess();
    } catch (err) {
      console.error('Form submission error:', err);
      setErrors({ general: 'Something went wrong. Please try again.' });
      trackError(err instanceof Error ? err.message : 'Unknown error', 'form_submission');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear field-specific error when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    // Track user interaction
    trackUserInteraction('form_input', field);
  };

  const inputClasses = darkMode
    ? "w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-300 focus:outline-none focus:border-blue-400 focus:bg-white/20 transition-all"
    : "w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all";

  const errorInputClasses = darkMode
    ? "w-full px-4 py-3 bg-white/10 border border-red-400 rounded-lg text-white placeholder-gray-300 focus:outline-none focus:border-red-400 focus:bg-white/20 transition-all"
    : "w-full px-4 py-3 border border-red-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-200 transition-all";

  const iconClasses = darkMode ? "text-gray-300" : "text-gray-400";

  return (
    <div>
      {/* General error message */}
      {errors.general && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm mb-4">
          {errors.general}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Honeypot field - hidden from users */}
        <input
          type="text"
          name="bot-field"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          style={{ display: 'none' }}
          tabIndex={-1}
          autoComplete="off"
        />

        <div className="relative">
          <Mail className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${iconClasses}`} />
          <input
            type="email"
            name="email"
            placeholder="Enter your email address"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            required
            aria-describedby={errors.email ? 'email-error' : undefined}
            className={`${errors.email ? errorInputClasses : inputClasses} pl-11`}
          />
          {errors.email && (
            <p id="email-error" className="mt-1 text-sm text-red-600" role="alert">
              {errors.email}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="relative">
            <User className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${iconClasses}`} />
            <input
              type="text"
              name="name"
              placeholder="Your name (optional)"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              aria-describedby={errors.name ? 'name-error' : undefined}
              className={`${errors.name ? errorInputClasses : inputClasses} pl-11`}
            />
            {errors.name && (
              <p id="name-error" className="mt-1 text-sm text-red-600" role="alert">
                {errors.name}
              </p>
            )}
          </div>

          <div className="relative">
            <Briefcase className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${iconClasses}`} />
            <input
              type="text"
              name="role"
              placeholder="Your role (optional)"
              value={formData.role}
              onChange={(e) => handleInputChange('role', e.target.value)}
              aria-describedby={errors.role ? 'role-error' : undefined}
              className={`${errors.role ? errorInputClasses : inputClasses} pl-11`}
            />
            {errors.role && (
              <p id="role-error" className="mt-1 text-sm text-red-600" role="alert">
                {errors.role}
              </p>
            )}
          </div>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className={`w-full py-3 px-6 rounded-lg font-medium transition-all flex items-center justify-center ${
            darkMode
              ? 'bg-white text-blue-600 hover:bg-gray-100'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {isSubmitting ? (
            <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              Join the waitlist
              <ArrowRight className="w-5 h-5 ml-2" />
            </>
          )}
        </button>

        <p className={`text-xs text-center ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
          Free forever. No spam. Unsubscribe anytime.
        </p>
      </form>
    </div>
  );
};

export default EmailForm;